import type { BackofficeMetricUnit } from '@backoffice-frontend/graphql'
import { useCallback } from 'react'
import { useTrackBackofficeMetricMutation } from '../graphql/mutation/trackBackofficeMetric.hook'

export interface MetricDimension {
  name: string
  value: string
}

export interface TrackMetricOptions {
  metricName: string
  value: number
  unit?: BackofficeMetricUnit
  dimensions?: MetricDimension[]
  namespace?: string
}

export interface TrackMetricsOptions {
  metrics: Array<{
    metricName: string
    value: number
    unit?: BackofficeMetricUnit
    dimensions?: MetricDimension[]
  }>
  namespace?: string
}

const validateMetricName = (metricName: string): void => {
  if (!metricName || typeof metricName !== 'string') {
    throw new Error('Metric name is required and must be a non-empty string')
  }
  if (metricName.trim().length === 0) {
    throw new Error('Metric name cannot be empty or only whitespace')
  }
}

const validateValue = (value: number): void => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error('Value is required and must be a finite number')
  }
}

const validateDimensions = (dimensions?: MetricDimension[]): void => {
  if (dimensions) {
    if (!Array.isArray(dimensions)) {
      throw new Error('Dimensions must be an array')
    }

    for (const dimension of dimensions) {
      if (dimension.name.trim().length === 0) {
        throw new Error('Each dimension must have a non-empty name')
      }
      if (dimension.value.trim().length === 0) {
        throw new Error('Each dimension must have a non-empty value')
      }
    }
  }
}

export const sanitizeUserAgent = (userAgent: string): string => {
  // Limit length for practical purposes
  return userAgent.substring(0, 200)
}

export const getUserAgent = (): string => {
  // Guard against SSR and non-browser environments
  if (typeof navigator === 'undefined' || !navigator.userAgent) {
    return 'unknown'
  }
  return sanitizeUserAgent(navigator.userAgent)
}

/**
 * A React hook for tracking metrics in the backoffice application.
 *
 * This hook provides a convenient way to send metric data to CloudWatch
 * for analytics and monitoring purposes. It handles validation, formatting,
 * and error handling for metric tracking operations using the new GraphQL schema.
 *
 * @example
 * ```tsx
 * const { trackMetric, trackMetrics } = useMetricTracking();
 *
 * // Track a single metric
 * const handleButtonClick = async () => {
 *   try {
 *     await trackMetric({
 *       metricName: 'button_click_count',
 *       value: 1,
 *       unit: BackofficeMetricUnit.Count,
 *       dimensions: [
 *         { name: 'buttonId', value: 'submit-form' },
 *         { name: 'userId', value: '12345' }
 *       ],
 *       namespace: 'Backoffice/Frontend'
 *     });
 *   } catch (error) {
 *     console.error('Failed to track metric:', error);
 *   }
 * };
 *
 * // Track multiple metrics at once
 * const handlePageLoad = async () => {
 *   try {
 *     await trackMetrics({
 *       metrics: [
 *         { metricName: 'page_load', value: 1, unit: BackofficeMetricUnit.Count },
 *         { metricName: 'load_time_ms', value: 250, unit: BackofficeMetricUnit.Milliseconds }
 *       ],
 *       namespace: 'Backoffice/Frontend'
 *     });
 *   } catch (error) {
 *     console.error('Failed to track metrics:', error);
 *   }
 * };
 * ```
 *
 * @returns An object containing the trackMetric and trackMetrics functions
 * @returns trackMetric - Async function to track a single metric with the provided options
 * @returns trackMetrics - Async function to track multiple metrics with the provided options
 *
 * @throws {Error} When metric name is invalid (empty, null, or not a string)
 * @throws {Error} When value is invalid (not a finite number)
 * @throws {Error} When dimensions are invalid (invalid structure or empty names/values)
 * @throws {Error} When GraphQL mutation fails or returns errors
 *
 * @see {@link TrackMetricOptions} for the structure of single metric tracking options
 * @see {@link TrackMetricsOptions} for the structure of batch metric tracking options
 * @see {@link MetricDimension} for the structure of metric dimensions
 */
export const useMetricTracking = () => {
  const [trackBackofficeMetricMutation] = useTrackBackofficeMetricMutation()

  const trackMetric = useCallback(
    async ({
      metricName,
      value,
      unit = 'COUNT' as BackofficeMetricUnit,
      dimensions = [],
      namespace = 'Backoffice/Frontend',
    }: TrackMetricOptions) => {
      validateMetricName(metricName)
      validateValue(value)
      validateDimensions(dimensions)

      const result = await trackBackofficeMetricMutation({
        variables: {
          input: {
            namespace,
            metrics: [
              {
                name: metricName.trim(),
                value,
                unit,
                dimensions: dimensions.map(({ name, value }) => ({
                  name: name.trim(),
                  value: value.trim(),
                })),
              },
            ],
            metadata: {
              userAgent: getUserAgent(),
              timestamp: new Date().toISOString(),
            },
          },
        },
      })

      if (result.errors) {
        throw new Error(
          `GraphQL error occurred: ${JSON.stringify(result.errors)}`,
        )
      }

      return result.data?.trackBackofficeMetric ?? false
    },
    [trackBackofficeMetricMutation],
  )

  const trackMetrics = useCallback(
    async ({
      metrics,
      namespace = 'Backoffice/Frontend',
    }: TrackMetricsOptions) => {
      if (!metrics || !Array.isArray(metrics) || metrics.length === 0) {
        throw new Error('Metrics array is required and must not be empty')
      }

      // Validate all metrics before sending
      for (const metric of metrics) {
        validateMetricName(metric.metricName)
        validateValue(metric.value)
        validateDimensions(metric.dimensions)
      }

      const result = await trackBackofficeMetricMutation({
        variables: {
          input: {
            namespace,
            metrics: metrics.map(
              ({
                metricName,
                value,
                unit = 'COUNT' as BackofficeMetricUnit,
                dimensions = [],
              }) => ({
                name: metricName.trim(),
                value,
                unit,
                dimensions: dimensions.map(({ name, value }) => ({
                  name: name.trim(),
                  value: value.trim(),
                })),
              }),
            ),
            metadata: {
              userAgent: getUserAgent(),
              timestamp: new Date().toISOString(),
            },
          },
        },
      })

      if (result.errors) {
        throw new Error(
          `GraphQL error occurred: ${JSON.stringify(result.errors)}`,
        )
      }

      return result.data?.trackBackofficeMetric ?? false
    },
    [trackBackofficeMetricMutation],
  )

  return { trackMetric, trackMetrics }
}
